import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useSocket } from "../../contexts/SocketContext";
import { useToast } from "../../contexts/ToastContext";
import api from "../../utils/api/axiosInstance";
import MessagesPanelSkeleton from "./MessagesPanelSkeleton";
import ConfirmModal from "../common/ConfirmModal";
import { FaEnvelope } from "react-icons/fa";
import styles from "../../styles/components/profile/MessagesPanel.module.css";
import { normalizeProfileUrl } from "../../utils/profilePaths";
import { toSameOriginPath } from "../../utils/safeAppUrl";
import ConversationList from "./messages/ConversationList";
import SystemMessageThread from "./messages/SystemMessageThread";
import MessageThread from "./messages/MessageThread";
import {
  matchesConversationSearch,
  matchesSystemSearch,
  idsEqual,
  isCurrentUserMessage,
} from "./messages/messageUtils";

const MessagesPanel = ({
  onUnreadChange,
  initialStartConversationWith,
  initialMessage,
  initialListingId = null,
  scope = "community",
  title = "Messages",
  variant = "standalone",
}) => {
  const { user: currentUser } = useUserAuth();
  const { socket, isConnected } = useSocket();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [systemMessages, setSystemMessages] = useState([]);
  const [systemMessagesUnread, setSystemMessagesUnread] = useState(0);
  const [systemThreadOpen, setSystemThreadOpen] = useState(false);
  const [messageText, setMessageText] = useState(initialMessage || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesListRef = useRef(null);
  const systemMessagesListRef = useRef(null);
  const messagesWrapperRef = useRef(null);
  const pendingSystemMessageIdRef = useRef(null);
  const lastFetchedConversationIdRef = useRef(null);
  const startConversationDismissedRef = useRef(false);
  const initialStartUserId = initialStartConversationWith
    ? (
        initialStartConversationWith._id || initialStartConversationWith
      )?.toString()
    : null;
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimeoutRef = useRef(null);
  const typingEmittedRef = useRef(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConversationId, setDeleteConversationId] = useState(null);

  // Review modal state
  const [reviewModal, setReviewModal] = useState(false);
  const [reviewedSet, setReviewedSet] = useState(new Set());
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewDims, setReviewDims] = useState({ accuracy: "", communication: "", shipping: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  const isMarketplaceScope = scope === "marketplace";
  const conversationQueryParams = scope === "all" ? {} : { scope };
  const threadOpen = systemThreadOpen || !!selectedConversation;

  const findConversationWithUser = (recipientId, listingId = null) =>
    conversations.find((c) => {
      const sameUser =
        c.otherUser?._id?.toString() === recipientId?.toString();
      if (!sameUser) return false;
      if (isMarketplaceScope && listingId) {
        const convListingId = c.listing?._id || c.listing;
        return convListingId?.toString() === listingId.toString();
      }
      return true;
    });

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredConversations = useMemo(() => {
    if (!normalizedSearch) return conversations;
    return conversations.filter((conv) =>
      matchesConversationSearch(conv, normalizedSearch),
    );
  }, [conversations, normalizedSearch]);

  const showSystemItem =
    !isMarketplaceScope &&
    (!normalizedSearch || matchesSystemSearch(systemMessages, normalizedSearch));

  const scrollChatAreaIntoView = useCallback(() => {
    window.requestAnimationFrame(() => {
      messagesWrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const scrollThreadContentToEnd = useCallback((systemMessageId = null) => {
    window.setTimeout(() => {
      scrollChatAreaIntoView();

      if (systemMessageId) {
        document
          .getElementById(`system-message-${systemMessageId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "end" });
        return;
      }

      if (systemThreadOpen && systemMessagesListRef.current) {
        systemMessagesListRef.current.scrollTop =
          systemMessagesListRef.current.scrollHeight;
        return;
      }

      if (messagesListRef.current) {
        messagesListRef.current.scrollTop =
          messagesListRef.current.scrollHeight;
      }
    }, 150);
  }, [scrollChatAreaIntoView, systemThreadOpen]);

  const scrollToBottom = () => {
    scrollThreadContentToEnd();
  };

  const clearMessageNavigationState = useCallback(() => {
    const hasNavigationState =
      location.state?.startConversationWith ||
      location.state?.initialMessage ||
      location.state?.conversationId ||
      location.state?.openSystemThread ||
      location.state?.systemMessageId ||
      location.state?.listingId;

    if (!hasNavigationState) {
      return;
    }

    const nextState = { ...(location.state || {}) };
    delete nextState.startConversationWith;
    delete nextState.initialMessage;
    delete nextState.conversationId;
    delete nextState.openSystemThread;
    delete nextState.systemMessageId;
    delete nextState.listingId;
    navigate(location.pathname, { replace: true, state: nextState });
  }, [location.pathname, location.state, navigate]);

  const handleCloseThread = () => {
    setSelectedConversation(null);
    setSystemThreadOpen(false);
    setMessages([]);

    if (initialStartConversationWith) {
      startConversationDismissedRef.current = true;
      clearMessageNavigationState();
    }
  };

  const applyStartConversation = (otherUser, listingId = initialListingId) => {
    if (!otherUser) return;

    const recipientId = otherUser._id || otherUser;
    const existingConv = findConversationWithUser(recipientId, listingId);

    setSystemThreadOpen(false);

    if (existingConv) {
      setSelectedConversation(existingConv);
      return;
    }

    if (
      typeof otherUser === "object" &&
      (otherUser.firstName || otherUser.username)
    ) {
      setSelectedConversation({
        otherUser,
        ...(listingId ? { listing: { _id: listingId } } : {}),
      });
      return;
    }

    setSelectedConversation({
      otherUser: { _id: recipientId },
      ...(listingId ? { listing: { _id: listingId } } : {}),
    });
  };

  useEffect(() => {
    if (initialMessage) {
      setMessageText(initialMessage);
    }
  }, [initialMessage]);

  // Load conversations once on mount.
  useEffect(() => {
    fetchConversations();
    if (!isMarketplaceScope) {
      fetchSystemMessages();
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (typingEmittedRef.current && socket && isConnected) {
        const recipientId =
          selectedConversation?.otherUser?._id ||
          selectedConversation?.otherUser;
        const conversationId = selectedConversation?._id;
        if (recipientId && conversationId) {
          socket.emit("typing_stop", {
            conversationId,
            recipientId: recipientId.toString(),
          });
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, []);

  const selectedConversationId = selectedConversation?._id || null;

  const activeConversation = useMemo(() => {
    if (!selectedConversation) return null;
    if (!selectedConversationId) return selectedConversation;
    const fromList = conversations.find(
      (c) => c._id?.toString() === selectedConversationId.toString(),
    );
    return fromList ? { ...selectedConversation, ...fromList } : selectedConversation;
  }, [conversations, selectedConversation, selectedConversationId]);

  useEffect(() => {
    if (!threadOpen) return;
    scrollChatAreaIntoView();
  }, [threadOpen, scrollChatAreaIntoView]);

  useEffect(() => {
    if (!threadOpen || loadingMessages) return;

    if (selectedConversationId && messages.length > 0) {
      scrollThreadContentToEnd();
    }
  }, [
    threadOpen,
    loadingMessages,
    selectedConversationId,
    messages.length,
    scrollThreadContentToEnd,
  ]);

  useEffect(() => {
    if (!systemThreadOpen || systemMessages.length === 0) return;
    if (pendingSystemMessageIdRef.current) return;

    scrollThreadContentToEnd();
  }, [
    systemThreadOpen,
    systemMessages.length,
    scrollThreadContentToEnd,
  ]);

  useEffect(() => {
    if (!selectedConversationId) {
      lastFetchedConversationIdRef.current = null;
      return;
    }

    if (lastFetchedConversationIdRef.current === selectedConversationId) {
      return;
    }

    lastFetchedConversationIdRef.current = selectedConversationId;
    setSystemThreadOpen(false);
    setMessages([]);
    fetchMessages(selectedConversationId);

    if (socket && isConnected) {
      socket.emit("join_conversation", selectedConversationId);
    }

    return () => {
      if (socket && isConnected) {
        socket.emit("leave_conversation", selectedConversationId);
      }
    };
    // fetchMessages is stable for the selected conversation lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId, socket, isConnected]);

  useEffect(() => {
    startConversationDismissedRef.current = false;
  }, [initialStartUserId]);

  useEffect(() => {
    if (!initialStartConversationWith || loading) return;
    if (startConversationDismissedRef.current) return;

    const recipientId = (
      initialStartConversationWith._id || initialStartConversationWith
    )?.toString();
    if (!recipientId) return;

    const currentOtherId = (
      selectedConversation?.otherUser?._id || selectedConversation?.otherUser
    )?.toString();

    const existingConv = findConversationWithUser(
      recipientId,
      initialListingId,
    );

    if (existingConv) {
      if (selectedConversationId !== existingConv._id) {
        setSystemThreadOpen(false);
        setSelectedConversation(existingConv);
      }
      return;
    }

    if (currentOtherId !== recipientId) {
      applyStartConversation(
        initialStartConversationWith,
        initialListingId,
      );
    }
    // Conversation bootstrap runs when navigation inputs change, not when helpers are recreated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialStartConversationWith,
    initialListingId,
    loading,
    conversations,
    selectedConversationId,
    isMarketplaceScope,
  ]);

  useEffect(() => {
    const conversationId = location.state?.conversationId;
    if (!conversationId || loading) return;

    const conversation = conversations.find(
      (conv) => String(conv._id) === String(conversationId),
    );

    if (!conversation) return;

    setSystemThreadOpen(false);
    setSelectedConversation(conversation);
    clearMessageNavigationState();
  }, [location.state?.conversationId, conversations, loading, clearMessageNavigationState]);

  useEffect(() => {
    if (!location.state?.openSystemThread || isMarketplaceScope) return;

    const systemMessageId = location.state?.systemMessageId;
    pendingSystemMessageIdRef.current = systemMessageId || null;

    setSelectedConversation(null);
    setSystemThreadOpen(true);

    if (!systemMessageId) {
      clearMessageNavigationState();
      return;
    }

    const delivery = systemMessages.find(
      (item) => String(item._id) === String(systemMessageId),
    );
    if (!delivery) return;

    const openSpecificSystemMessage = async () => {
      if (!delivery.read) {
        try {
          await api.post(`/users/me/system-messages/${systemMessageId}/read`);
          setSystemMessages((prev) =>
            prev.map((item) =>
              String(item._id) === String(systemMessageId)
                ? {
                    ...item,
                    read: true,
                    readAt: new Date().toISOString(),
                  }
                : item,
            ),
          );
          setSystemMessagesUnread((count) => Math.max(0, count - 1));
        } catch (err) {
          console.error("Error marking system message read:", err);
        }
      }

      scrollThreadContentToEnd(systemMessageId);
      pendingSystemMessageIdRef.current = null;
      clearMessageNavigationState();
    };

    openSpecificSystemMessage();
  }, [
    location.state?.openSystemThread,
    location.state?.systemMessageId,
    systemMessages,
    isMarketplaceScope,
    clearMessageNavigationState,
    scrollThreadContentToEnd,
  ]);

  // Socket listeners intentionally omit fetch helpers to avoid re-subscribing each render.
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewMessage = (data) => {
      const { message, conversation } = data;

      if (idsEqual(selectedConversation?._id, conversation._id)) {
        setMessages((prev) => {
          const exists = prev.some((m) => m._id === message._id);
          if (exists) return prev;
          return [...prev, message];
        });
        scrollToBottom();

        if (idsEqual(message.recipient, currentUser)) {
          markConversationRead(conversation._id);
        }
      }

      setConversations((prev) => {
        const updated = prev.map((conv) =>
          conv._id === conversation._id
            ? {
                ...conv,
                lastMessage: message,
                lastMessageAt: conversation.lastMessageAt,
                messageCount:
                  conversation.messageCount ?? conv.messageCount,
              }
            : conv,
        );

        const exists = updated.some((c) => c._id === conversation._id);
        if (!exists) {
          fetchConversations({ silent: true });
          return updated;
        }

        return updated.sort(
          (a, b) =>
            new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0),
        );
      });

      if (
        idsEqual(selectedConversation?._id, conversation._id) &&
        conversation.messageCount != null
      ) {
        setSelectedConversation((prev) =>
          prev
            ? { ...prev, messageCount: conversation.messageCount }
            : prev,
        );
      }
    };

    const handleMessageReceived = (data) => {
      const { message } = data;
      if (idsEqual(selectedConversation?._id, message.conversation)) {
        setMessages((prev) => {
          const exists = prev.some((m) => m._id === message._id);
          if (exists) return prev;
          return [...prev, message];
        });
        scrollToBottom();

        if (idsEqual(message.recipient, currentUser)) {
          markConversationRead(message.conversation);
        }
      }
    };

    const handleConversationUpdated = (data) => {
      const { conversation } = data;
      setConversations((prev) => {
        const updated = prev.map((conv) =>
          conv._id === conversation._id
            ? {
                ...conv,
                lastMessage: conversation.lastMessage,
                lastMessageAt: conversation.lastMessageAt,
                messageCount: conversation.messageCount ?? conv.messageCount,
              }
            : conv,
        );

        return updated.sort(
          (a, b) =>
            new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0),
        );
      });

      if (idsEqual(selectedConversation?._id, conversation._id)) {
        setSelectedConversation((prev) =>
          prev
            ? {
                ...prev,
                lastMessageAt: conversation.lastMessageAt,
                messageCount: conversation.messageCount ?? prev.messageCount,
              }
            : prev,
        );
      }
    };

    const handleUserTyping = (data) => {
      if (selectedConversation?._id === data.conversationId) {
        setTypingUsers((prev) => ({
          ...prev,
          [data.userId]: data.userName,
        }));
      }
    };

    const handleUserStoppedTyping = (data) => {
      if (selectedConversation?._id === data.conversationId) {
        setTypingUsers((prev) => {
          const updated = { ...prev };
          delete updated[data.userId];
          return updated;
        });
      }
    };

    const handleMessagesRead = (data) => {
      if (!idsEqual(selectedConversation?._id, data.conversationId)) return;

      setMessages((prev) =>
        prev.map((msg) =>
          isCurrentUserMessage(msg, currentUser)
            ? { ...msg, read: true, readAt: new Date().toISOString() }
            : msg,
        ),
      );
    };

    const handleNewSystemMessage = (data) => {
      if (isMarketplaceScope) return;
      fetchSystemMessages();
      showToast({
        message: `New system message: ${data.subject}`,
        type: "info",
      });
    };

    socket.on("new_message", handleNewMessage);
    socket.on("message_received", handleMessageReceived);
    socket.on("conversation_updated", handleConversationUpdated);
    socket.on("user_typing", handleUserTyping);
    socket.on("user_stopped_typing", handleUserStoppedTyping);
    socket.on("messages_read", handleMessagesRead);
    socket.on("new_system_message", handleNewSystemMessage);

    return () => {
      socket.off("new_message", handleNewMessage);
      socket.off("message_received", handleMessageReceived);
      socket.off("conversation_updated", handleConversationUpdated);
      socket.off("user_typing", handleUserTyping);
      socket.off("user_stopped_typing", handleUserStoppedTyping);
      socket.off("messages_read", handleMessagesRead);
      socket.off("new_system_message", handleNewSystemMessage);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable socket subscription scope
  }, [socket, isConnected, selectedConversation, currentUser]);

  const fetchConversations = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const res = await api.get("/messages/conversations", {
        params: conversationQueryParams,
      });
      setConversations(res.data.conversations || []);

      const totalUnread = (res.data.conversations || []).reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0,
      );
      if (onUnreadChange) {
        onUnreadChange(
          totalUnread + (isMarketplaceScope ? 0 : systemMessagesUnread),
        );
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
      if (!silent) {
        showToast({
          message:
            err.response?.data?.message || "Failed to load conversations",
          type: "error",
        });
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchSystemMessages = async () => {
    try {
      const res = await api.get("/users/me/system-messages", {
        params: { page: 1, limit: 50 },
      });
      setSystemMessages(res.data.messages || []);

      const unreadRes = await api.get("/users/me/system-messages/unread-count");
      const count = unreadRes.data.count || 0;
      setSystemMessagesUnread(count);
      if (onUnreadChange) {
        const conversationUnread = conversations.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0,
        );
        onUnreadChange(conversationUnread + count);
      }
    } catch (err) {
      console.error("Error fetching system messages:", err);
    }
  };

  useEffect(() => {
    if (!onUnreadChange) return;
    const conversationUnread = conversations.reduce(
      (sum, conv) => sum + (conv.unreadCount || 0),
      0,
    );
    onUnreadChange(
      conversationUnread + (isMarketplaceScope ? 0 : systemMessagesUnread),
    );
  }, [
    conversations,
    systemMessagesUnread,
    onUnreadChange,
    isMarketplaceScope,
  ]);

  const markSystemMessagesRead = async () => {
    const unread = systemMessages.filter((item) => !item.read);
    if (unread.length === 0) return;
    try {
      await Promise.all(
        unread.map((item) =>
          api.post(`/users/me/system-messages/${item._id}/read`),
        ),
      );
      setSystemMessages((prev) =>
        prev.map((item) =>
          item.read
            ? item
            : { ...item, read: true, readAt: new Date().toISOString() },
        ),
      );
      setSystemMessagesUnread(0);
    } catch (err) {
      console.error("Error marking system messages read:", err);
    }
  };

  const handleOpenSystemThread = async () => {
    pendingSystemMessageIdRef.current = null;
    setSelectedConversation(null);
    setSystemThreadOpen(true);
    await markSystemMessagesRead();
    scrollThreadContentToEnd();
  };

  const handleSystemMessageAction = (actionUrl) => {
    const path = toSameOriginPath(actionUrl);
    if (!path) return;
    navigate(normalizeProfileUrl(path, currentUser));
  };

  const markConversationRead = async (conversationId) => {
    if (!conversationId) return;
    try {
      await api.post(`/messages/conversations/${conversationId}/read`);
    } catch (err) {
      console.error("Error marking conversation read:", err);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      setLoadingMessages(true);

      const res = await api.get(
        `/messages/conversations/${conversationId}/messages`,
        {
          params: { page: 1, limit: 100 },
        },
      );

      setMessages(res.data.messages || []);
      scrollToBottom();

      // Sync unread badge locally; avoid refetching conversations (causes loops)
      setConversations((prev) =>
        prev.map((conv) =>
          conv._id === conversationId ? { ...conv, unreadCount: 0 } : conv,
        ),
      );
    } catch (err) {
      console.error("Error fetching messages:", err);
      showToast({
        message: err.response?.data?.message || "Failed to load messages",
        type: "error",
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    setMessageText(value);

    if (!selectedConversation || !socket || !isConnected) return;

    const recipientId =
      selectedConversation.otherUser?._id || selectedConversation.otherUser;
    if (!recipientId) return;

    const conversationId = selectedConversation._id;

    if (value.trim() && !typingEmittedRef.current) {
      typingEmittedRef.current = true;
      socket.emit("typing_start", {
        conversationId,
        recipientId: recipientId.toString(),
      });
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (typingEmittedRef.current) {
        typingEmittedRef.current = false;
        socket.emit("typing_stop", {
          conversationId,
          recipientId: recipientId.toString(),
        });
      }
    }, 3000);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversation || sending) return;

    const recipientId =
      selectedConversation.otherUser?._id || selectedConversation.otherUser;
    if (!recipientId) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    if (typingEmittedRef.current && socket && isConnected) {
      typingEmittedRef.current = false;
      const conversationId = selectedConversation._id;
      socket.emit("typing_stop", {
        conversationId,
        recipientId: recipientId.toString(),
      });
    }

    try {
      setSending(true);
      const listingId =
        selectedConversation.listing?._id ||
        selectedConversation.listing ||
        initialListingId;
      const res = await api.post("/messages/send", {
        recipientId,
        text: messageText.trim(),
        ...(isMarketplaceScope && listingId ? { listingId } : {}),
      });

      setMessages((prev) => {
        const exists = prev.some((m) => m._id === res.data._id);
        if (exists) return prev;
        return [...prev, res.data];
      });
      setMessageText("");
      scrollToBottom();

      const convRes = await api.get("/messages/conversations", {
        params: conversationQueryParams,
      });
      const updatedConversations = convRes.data.conversations || [];
      setConversations(updatedConversations);

      const recipientIdStr = recipientId.toString();
      const updatedConv = updatedConversations.find(
        (c) =>
          c.otherUser?._id?.toString() === recipientIdStr &&
          (!isMarketplaceScope ||
            !listingId ||
            (c.listing?._id || c.listing)?.toString() === listingId.toString()),
      );
      if (updatedConv) {
        setSelectedConversation(updatedConv);
      }

      const totalUnread = updatedConversations.reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0,
      );
      if (onUnreadChange) {
        onUnreadChange(
          totalUnread + (isMarketplaceScope ? 0 : systemMessagesUnread),
        );
      }
    } catch (err) {
      console.error("Error sending message:", err);
      showToast({
        message: err.response?.data?.message || "Failed to send message",
        type: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const handleDeleteConversation = (conversationId, e) => {
    e.stopPropagation();
    setDeleteConversationId(conversationId);
    setShowDeleteModal(true);
  };

  const confirmDeleteConversation = async () => {
    if (!deleteConversationId) return;

    try {
      setShowDeleteModal(false);
      await api.delete(`/messages/conversations/${deleteConversationId}`);

      setConversations((prev) => {
        const filtered = prev.filter(
          (conv) => conv._id !== deleteConversationId,
        );

        const remainingUnread = filtered.reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0,
        );
        if (onUnreadChange) {
          onUnreadChange(
            remainingUnread +
              (isMarketplaceScope ? 0 : systemMessagesUnread),
          );
        }

        return filtered;
      });

      if (selectedConversation?._id === deleteConversationId) {
        handleCloseThread();
      }

      showToast({
        message: "Conversation deleted successfully",
        type: "success",
      });
    } catch (err) {
      console.error("Error deleting conversation:", err);
      showToast({
        message: err.response?.data?.message || "Failed to delete conversation",
        type: "error",
      });
    }
  };

  const REVIEW_THRESHOLD = 5;
  const currentUserId = currentUser?._id?.toString();
  const listingSellerId =
    activeConversation?.listing?.seller?._id?.toString() ||
    activeConversation?.listing?.seller?.toString();
  const isSellerInConversation =
    !!listingSellerId && listingSellerId === currentUserId;
  const isBuyerInConversation =
    isMarketplaceScope &&
    !!activeConversation?.listing &&
    !isSellerInConversation &&
    !!currentUserId;
  const isReviewEligible =
    isBuyerInConversation &&
    (activeConversation.messageCount || 0) >= REVIEW_THRESHOLD &&
    !reviewedSet.has(activeConversation.listing?._id?.toString());
  const showConversationMenu = isBuyerInConversation;

  const openReviewModal = () => {
    setReviewRating(0);
    setReviewComment("");
    setReviewDims({ accuracy: "", communication: "", shipping: "" });
    setReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewRating || !activeConversation?.listing?._id) return;
    try {
      setSubmittingReview(true);
      const dims = {};
      if (reviewDims.accuracy) dims.accuracy = Number(reviewDims.accuracy);
      if (reviewDims.communication) dims.communication = Number(reviewDims.communication);
      if (reviewDims.shipping) dims.shipping = Number(reviewDims.shipping);
      await api.post(`/listings/${activeConversation.listing._id}/review`, {
        rating: reviewRating,
        comment: reviewComment.trim(),
        dimensions: dims,
      });
      setReviewedSet((prev) => new Set([...prev, activeConversation.listing._id.toString()]));
      setReviewModal(false);
    } catch (err) {
      showToast({
        message: err.response?.data?.message || "Failed to submit review",
        type: "error",
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return <MessagesPanelSkeleton variant={variant} />;
  }

  return (
    <div
      ref={messagesWrapperRef}
      className={`${styles.messagesWrapper} ${
        variant === "embedded" ? styles.embedded : ""
      }`}
    >
      <div
        className={`${styles.messagesContainer} ${
          threadOpen ? styles.threadOpen : ""
        }`}
      >
        <ConversationList
          title={title}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          conversations={filteredConversations}
          systemMessages={systemMessages}
          systemMessagesUnread={systemMessagesUnread}
          systemThreadOpen={systemThreadOpen}
          selectedConversationId={selectedConversation?._id}
          isMarketplaceScope={isMarketplaceScope}
          showSystemItem={showSystemItem}
          onOpenSystemThread={handleOpenSystemThread}
          onSelectConversation={(conv) => {
            pendingSystemMessageIdRef.current = null;
            setSelectedConversation(conv);
            setSystemThreadOpen(false);
            scrollChatAreaIntoView();
          }}
          onDeleteConversation={handleDeleteConversation}
        />

        {systemThreadOpen ? (
          <SystemMessageThread
            systemMessages={systemMessages}
            messagesListRef={systemMessagesListRef}
            onBack={handleCloseThread}
            onAction={handleSystemMessageAction}
          />
        ) : selectedConversation ? (
          <MessageThread
            currentUser={currentUser}
            isMarketplaceScope={isMarketplaceScope}
            loadingMessages={loadingMessages}
            messageText={messageText}
            messages={messages}
            messagesListRef={messagesListRef}
            onBack={handleCloseThread}
            onMessageInputChange={handleMessageInputChange}
            onSendMessage={handleSendMessage}
            selectedConversation={activeConversation}
            sending={sending}
            typingUsers={typingUsers}
            reviewEligible={isReviewEligible}
            showHeaderMenu={showConversationMenu}
            onLeaveReview={openReviewModal}
          />
        ) : (
          <div className={styles.noSelection}>
            <FaEnvelope />
            <p>Select a conversation or system message</p>
          </div>
        )}

      </div>

      {showDeleteModal && (
        <ConfirmModal
          message="Are you sure you want to delete this conversation? This will only remove it from your side."
          onConfirm={confirmDeleteConversation}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {reviewModal && (
        <div className={styles.reviewModalBackdrop} onClick={() => setReviewModal(false)}>
          <div className={styles.reviewModal} onClick={(e) => e.stopPropagation()}>
            <div>
              <p className={styles.reviewModalTitle}>Leave a Seller Review</p>
              <p className={styles.reviewModalSeller}>
                {activeConversation?.listing?.seller?.firstName ||
                  activeConversation?.otherUser?.firstName}{" "}
                {activeConversation?.listing?.seller?.lastName ||
                  activeConversation?.otherUser?.lastName}
                {activeConversation?.listing?.title && (
                  <> · {activeConversation.listing.title}</>
                )}
              </p>
            </div>

            <div className={styles.reviewStarsRow}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`${styles.reviewStar} ${reviewRating >= star ? styles.active : ""}`}
                  onClick={() => setReviewRating(star)}
                  aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                >
                  ★
                </button>
              ))}
            </div>

            <div className={styles.reviewDimsGrid}>
              {["accuracy", "communication", "shipping"].map((dim) => (
                <div key={dim}>
                  <span className={styles.reviewDimLabel}>
                    {dim.charAt(0).toUpperCase() + dim.slice(1)}
                  </span>
                  <select
                    className={styles.reviewDimSelect}
                    value={reviewDims[dim]}
                    onChange={(e) => setReviewDims((prev) => ({ ...prev, [dim]: e.target.value }))}
                  >
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5].map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <textarea
              className={styles.reviewCommentArea}
              placeholder="Share your experience (optional)..."
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              maxLength={500}
            />

            <div className={styles.reviewModalActions}>
              <button
                type="button"
                className={styles.reviewCancelBtn}
                onClick={() => setReviewModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.reviewSubmitBtn}
                disabled={!reviewRating || submittingReview}
                onClick={handleSubmitReview}
              >
                {submittingReview ? "Submitting…" : "Submit Review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPanel;
